import { EventEmitter } from "fbemitter";
import { Key, EntityType } from "../../../../models";
import { Command, Command1, Command2 } from "../../../../Command";
import { Polyglot } from "../../../../polyglot";
import { appNavigator } from "../../../routes";
import { getSearchName, EntitySearchResult, SearchFn } from "../../../../rest/Search";
import { ErrorActions } from "../../../flux/Error";
import { LabelActions } from "../../../flux/Label";
import { ListActions } from "../../../flux/Grid";
import { FlagActions } from "../../../flux/Flag";
import { ValueActions } from "../../../flux/Value";

export const getSearchNameMiddleware = (entityType: EntityType) => {
    return (params) => ({ ...params, Name: getSearchName(entityType) });
}

export const getCurrentDivisionMiddleware = (getDivision: () => number) => {
    return (params) => ({ ...params, DivisionId: getDivision() });
}

// This is a base class for a search. It is primary responsibility is to update the grid when result is available
class SearchBase
{
    protected readonly errorActions = new ErrorActions();
    protected readonly labelActions = new LabelActions();
    protected readonly listActions = new ListActions();

    private readonly transformators = [];

    labelDispatchToken: string; // value will come from another tab
    constructor(protected polyglot: Polyglot, protected entityType: EntityType, protected errorDispatchToken: string)
    {
        this.addRequestMiddleware(getSearchNameMiddleware(entityType));
    }

    protected handleResult = (res: EntitySearchResult) => {
        this.listActions.setItems(res.Entries[EntityType[this.entityType]], this.entityType);
        this.labelActions.set(res.SearchTitle || this.polyglot.t('misc.search_results'), this.labelDispatchToken);
        let url_parts = window.location.href.split("/");
        if(url_parts[url_parts.length - 1] != 'dashboard'){
            appNavigator.goToEntityTab(this.entityType);
        }
    }

    protected handleError = (err: Error) => this.errorActions.setError(err, this.errorDispatchToken, 5 * 1000);

    addRequestMiddleware = (middleware: (request: any) => any) => {
        this.transformators.push(middleware);
    }
    protected applyMiddleware = (params: any) => this.transformators.reduce((prev, transformation) => transformation(prev), params);
}

// Searches which are initiated from search box inside Entity tab are tight with a spinner
// This command shows and hiddes the spinner during the search as well as shows error message in case of search failure
export class SearchWithSpinnerCommand extends SearchBase implements Command1<any>
{
    private readonly inProgressActions = new FlagActions();
    constructor(polyglot: Polyglot, entityType: EntityType, private runSearch: SearchFn, private refresher: Refresher, private inProgressDispatchToken: string, errorDispatchToken: string)
    {
        super(polyglot, entityType, errorDispatchToken);
    }

    execute = (params: any) => {
        this.inProgressActions.set(this.inProgressDispatchToken);
        const targetParams = this.applyMiddleware(params);
        this.refresher.bindParameters(targetParams);
        this.runSearch(targetParams)
            .then(this.handleResult)
            .catch(this.handleError)
            .then(() => this.inProgressActions.reset(this.inProgressDispatchToken)); // finally
    }
}

// Searches which are initiated from grid are assosiated with the spinner in a particullar cell
// This class hepls to start/stop that spinner. Also search can be interrupted by another search from a different cell in a grid
// In this case we need to update the innitiator
export class SearchWithInitiatorCommand extends SearchBase implements Command2<Key, any>
{
    private readonly valueActions = new ValueActions<Key>();
    private initiator: Key;

    refresher: Refresher
    constructor(polyglot: Polyglot, entityType: EntityType, private runSearch: SearchFn, private valueDispatchToken: string, errorDispatchToken: string)
    {
        super(polyglot, entityType, errorDispatchToken);
    }

    execute = (initiator: Key, params: any) => {
        this.initiator = initiator;
        const sameInitiator = () => this.initiator === initiator;
        
        this.valueActions.set(initiator, this.valueDispatchToken);
        const targetParams = this.applyMiddleware(params);
        if(this.refresher){
            this.refresher.bindParameters(targetParams);
        }
        this.runSearch(targetParams)
            .then(res => sameInitiator() && this.handleResult(res))
            .catch(err => sameInitiator() && this.handleError(err))
            .then(() => {
                if(sameInitiator()) {
                    this.valueActions.clear(this.valueDispatchToken);
                    this.initiator = null;
                }
            });
    }
}

// We need to run the search on the page when current division has changed
// But we don't want to run all searches blindly, we need to run the search on the active tab only
export class Refresher
{
    private expired: boolean;
    private searchParameters: any;

    searchCmd: Command1<any>
    constructor(emiter: EventEmitter, private getDivision: () => number)
    {
        // subscribe on division change event
        emiter.addListener('division', () => {
            // mark all search results on each tab as expired in case this result depends on current division parameter
            this.expired = this.expired || this.dependsOnDivision(this.searchParameters);
        });
    }

    // remember search paramters
    bindParameters = (parameters: any, expired: boolean = false) => {
        this.searchParameters = parameters;
        this.expired = expired;
    }

    // run the search in case results marked as expired
    run = () => {
        if(this.expired) {
            // try to update implicit DivisionId param (in case search depends on it) then run new search
            this.searchCmd.execute(this.modernize(this.searchParameters));
        }
    }
    
    modernize = (params: any) => {
        if(this.dependsOnDivision(params)) {
            params = { ...params, DivisionId: this.getDivision() };
        }
        return params;
    }

    dependsOnDivision = (params: any) => params != null && 'DivisionId' in params;
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/commands/EntitySearchCommand.ts